import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  InputAdornment,
  Divider,
  Paper,
  alpha,
  useTheme,
  Grid,
} from "@mui/material";
import {
  Science,
  Grass,
  Terrain,
  WaterDrop,
  CheckCircle,
  ErrorOutline,
} from "@mui/icons-material";

// --- USDA TOPRAK ÜÇGENİ HESAPLAMA ALGORİTMASI ---
const calculateTextureClass = (sand, silt, clay) => {
  if (Math.round(sand + silt + clay) !== 100) return null;

  if (clay >= 40) {
    if (silt >= 40) return { code: "SIC", name: "Siltli Kil" };
    if (sand <= 45 && silt < 40) return { code: "C", name: "Kil" };
    if (sand >= 45) return { code: "SC", name: "Kumlu Kil" };
  }
  if (clay >= 27 && clay < 40) {
    if (sand > 45) return { code: "SC", name: "Kumlu Kil" };
    if (sand <= 20) return { code: "SICL", name: "Siltli Killi Tın" };
    return { code: "CL", name: "Killi Tın" };
  }
  if (clay >= 20 && clay < 27) {
    if (silt >= 50) return { code: "SIL", name: "Siltli Tın" };
    if (sand >= 52) return { code: "SCL", name: "Kumlu Killi Tın" };
    return { code: "L", name: "Tın" };
  }
  if (clay >= 7 && clay < 20) {
    if (silt >= 80) return { code: "SI", name: "Silt" };
    if (silt >= 50 && silt < 80) return { code: "SIL", name: "Siltli Tın" };
    if (silt >= 28 && silt < 50 && sand <= 52)
      return { code: "L", name: "Tın" };
    if (sand > 52 && silt + 2 * clay >= 30)
      return { code: "SL", name: "Tınlı Kum" };
  }
  if (clay < 7) {
    if (silt >= 80) return { code: "SI", name: "Silt" };
    if (silt >= 50 && silt < 80) return { code: "SIL", name: "Siltli Tın" };
    if (silt < 50 && sand > 43 && silt + 2 * clay >= 30)
      return { code: "SL", name: "Tınlı Kum" };
    if (sand >= 70 && silt + 1.5 * clay < 30 && silt + 2 * clay >= 15)
      return { code: "LS", name: "Kumlu Tın" };
    if (sand >= 85 && silt + 1.5 * clay < 15) return { code: "S", name: "Kum" };
  }

  if (clay >= 20 && clay < 35 && silt < 28 && sand > 45)
    return { code: "SCL", name: "Kumlu Killi Tın" };

  return { code: "?", name: "Sınıflandırılamadı" };
};

export default function TextureCalculatorPage() {
  const theme = useTheme();

  const [values, setValues] = useState({ sand: "", silt: "", clay: "" });
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState(null);
  const [markerPoint, setMarkerPoint] = useState(null);

  // SVG Üçgen Geometrisi (Barycentric -> Cartesian)
  const TRIANGLE_WIDTH = 400;
  const TRIANGLE_HEIGHT = 346.41; // 400 * sqrt(3)/2

  useEffect(() => {
    const s = parseFloat(values.sand) || 0;
    const si = parseFloat(values.silt) || 0;
    const c = parseFloat(values.clay) || 0;

    const sum = s + si + c;
    setTotal(sum);

    if (Math.round(sum) === 100) {
      setResult(calculateTextureClass(s, si, c));

      // Harita Üzerindeki Noktayı Hesapla (x, y)
      // USDA Üçgeni: Sol Alt = %100 Kum, Sağ Alt = %100 Silt, Tepe = %100 Kil
      const x = (si * TRIANGLE_WIDTH) / 100 + (c * (TRIANGLE_WIDTH / 2)) / 100;
      const y = TRIANGLE_HEIGHT - (c * TRIANGLE_HEIGHT) / 100;
      setMarkerPoint({ x, y });
    } else {
      setResult(null);
      setMarkerPoint(null);
    }
  }, [values]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const autoFillRemaining = (targetField) => {
    const currentKeys = ["sand", "silt", "clay"].filter(
      (k) => k !== targetField,
    );
    const sumOthers =
      (parseFloat(values[currentKeys[0]]) || 0) +
      (parseFloat(values[currentKeys[1]]) || 0);

    if (sumOthers <= 100) {
      setValues((prev) => ({
        ...prev,
        [targetField]: (100 - sumOthers).toFixed(1).replace(/\.0$/, ""),
      }));
    }
  };

  // Grid Çizgilerini Oluşturma Helper'ı
  const generateGridLines = () => {
    const lines = [];
    for (let i = 10; i < 100; i += 10) {
      // Kil (Yatay Çizgiler)
      const y = TRIANGLE_HEIGHT - (i * TRIANGLE_HEIGHT) / 100;
      const x1 = (i * (TRIANGLE_WIDTH / 2)) / 100;
      const x2 = TRIANGLE_WIDTH - x1;
      lines.push(
        <line
          key={`c${i}`}
          x1={x1}
          y1={y}
          x2={x2}
          y2={y}
          stroke="#e0e0e0"
          strokeWidth="1"
        />,
      );

      // Silt (Sola Yatık Çizgiler)
      const siX1 = (i * TRIANGLE_WIDTH) / 100;
      const siY1 = TRIANGLE_HEIGHT;
      const siX2 = siX1 / 2;
      const siY2 = TRIANGLE_HEIGHT - (siX1 * TRIANGLE_HEIGHT) / TRIANGLE_WIDTH;
      lines.push(
        <line
          key={`si${i}`}
          x1={siX1}
          y1={siY1}
          x2={siX2}
          y2={siY2}
          stroke="#e0e0e0"
          strokeWidth="1"
        />,
      );

      // Kum (Sağa Yatık Çizgiler)
      const saX1 = ((100 - i) * TRIANGLE_WIDTH) / 100;
      const saY1 = TRIANGLE_HEIGHT;
      const saX2 = saX1 + (i * (TRIANGLE_WIDTH / 2)) / 100;
      const saY2 = TRIANGLE_HEIGHT - (i * TRIANGLE_HEIGHT) / 100;
      lines.push(
        <line
          key={`sa${i}`}
          x1={saX1}
          y1={saY1}
          x2={saX2}
          y2={saY2}
          stroke="#e0e0e0"
          strokeWidth="1"
        />,
      );
    }
    return lines;
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        bgcolor: "#f4f6f8",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Card
        elevation={0}
        sx={{
          maxWidth: 900,
          width: "100%",
          borderRadius: 4,
          border: "1px solid #eaecf0",
          mt: 4,
          overflow: "hidden",
        }}
      >
        {/* BAŞLIK */}
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "white",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="800"
            fontFamily="Montserrat"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            <Science /> Toprak Tekstür (Bünye) Üçgeni
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
            Kum, Silt ve Kil yüzdelerini girin, toprak sınıfını ve üçgen
            üzerindeki konumunu anında görün.
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            {/* SOL KISIM: VERİ GİRİŞİ */}
            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                <TextField
                  label="Kum (Sand) %"
                  name="sand"
                  value={values.sand}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Terrain color="warning" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            fontWeight: "bold",
                          }}
                          onClick={() => autoFillRemaining("sand")}
                        >
                          Tamamla
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Silt (Silt) %"
                  name="silt"
                  value={values.silt}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WaterDrop color="info" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            fontWeight: "bold",
                          }}
                          onClick={() => autoFillRemaining("silt")}
                        >
                          Tamamla
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Kil (Clay) %"
                  name="clay"
                  value={values.clay}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Grass color="success" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="caption"
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            fontWeight: "bold",
                          }}
                          onClick={() => autoFillRemaining("clay")}
                        >
                          Tamamla
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* TOPLAM DURUMU */}
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color={
                      total === 100
                        ? "success.main"
                        : total > 100
                          ? "error.main"
                          : "text.secondary"
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {total === 100 ? (
                      <CheckCircle fontSize="small" />
                    ) : (
                      <ErrorOutline fontSize="small" />
                    )}
                    TOPLAM: %{total.toFixed(1).replace(/\.0$/, "")}
                    {total !== 100 && " (100 olmalıdır)"}
                  </Typography>

                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      bgcolor: "#eee",
                      borderRadius: 4,
                      mt: 1,
                      overflow: "hidden",
                      display: "flex",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min(parseFloat(values.sand) || 0, 100)}%`,
                        bgcolor: theme.palette.warning.main,
                      }}
                    />
                    <Box
                      sx={{
                        width: `${Math.min(parseFloat(values.silt) || 0, 100)}%`,
                        bgcolor: theme.palette.info.main,
                      }}
                    />
                    <Box
                      sx={{
                        width: `${Math.min(parseFloat(values.clay) || 0, 100)}%`,
                        bgcolor: theme.palette.success.main,
                      }}
                    />
                  </Box>
                </Box>

                {/* SONUÇ KUTUSU */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: 3,
                    border: "2px solid",
                    borderColor: result ? "success.light" : "grey.200",
                    bgcolor: result
                      ? alpha(theme.palette.success.light, 0.1)
                      : "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                  >
                    ANALİZ SONUCU
                  </Typography>
                  {result ? (
                    <Box>
                      <Typography
                        variant="h4"
                        color="success.dark"
                        fontWeight="900"
                        mt={0.5}
                      >
                        {result.code}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="text.primary"
                        fontWeight="bold"
                      >
                        {result.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontStyle: "italic" }}
                    >
                      Değerleri %100'e tamamlayın.
                    </Typography>
                  )}
                </Paper>
              </Stack>
            </Grid>

            {/* SAĞ KISIM: İNTERAKTİF ÜÇGEN */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                <svg
                  viewBox="-40 -20 480 400"
                  style={{ width: "100%", maxWidth: "400px" }}
                >
                  {/* Grid Çizgileri */}
                  {generateGridLines()}

                  {/* Ana Üçgen Çerçevesi */}
                  <polygon
                    points={`0,${TRIANGLE_HEIGHT} ${TRIANGLE_WIDTH},${TRIANGLE_HEIGHT} ${TRIANGLE_WIDTH / 2},0`}
                    fill="none"
                    stroke="#333"
                    strokeWidth="3"
                  />

                  {/* Eksen Etiketleri */}
                  <text
                    x={TRIANGLE_WIDTH / 2}
                    y={TRIANGLE_HEIGHT + 25}
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    fill="#ed6c02"
                  >
                    Kum (Sand) % &rarr;
                  </text>
                  <text
                    x={TRIANGLE_WIDTH / 4 - 20}
                    y={TRIANGLE_HEIGHT / 2}
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    fill="#2e7d32"
                    transform={`rotate(-60, ${TRIANGLE_WIDTH / 4 - 20}, ${TRIANGLE_HEIGHT / 2})`}
                  >
                    &larr; Kil (Clay) %
                  </text>
                  <text
                    x={(3 * TRIANGLE_WIDTH) / 4 + 20}
                    y={TRIANGLE_HEIGHT / 2}
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    fill="#0288d1"
                    transform={`rotate(60, ${(3 * TRIANGLE_WIDTH) / 4 + 20}, ${TRIANGLE_HEIGHT / 2})`}
                  >
                    &larr; Silt (Silt) %
                  </text>

                  {/* Uç Nokta Etiketleri */}
                  <text
                    x={TRIANGLE_WIDTH / 2}
                    y="-5"
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    100% Kil
                  </text>
                  <text
                    x="-15"
                    y={TRIANGLE_HEIGHT + 15}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    100% Kum
                  </text>
                  <text
                    x={TRIANGLE_WIDTH + 15}
                    y={TRIANGLE_HEIGHT + 15}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    100% Silt
                  </text>

                  {/* Dinamik Hedef Noktası */}
                  {markerPoint && (
                    <g>
                      <circle
                        cx={markerPoint.x}
                        cy={markerPoint.y}
                        r="8"
                        fill="#d32f2f"
                        stroke="#fff"
                        strokeWidth="2"
                      />
                      <circle
                        cx={markerPoint.x}
                        cy={markerPoint.y}
                        r="16"
                        fill="rgba(211, 47, 47, 0.3)"
                      />
                      <text
                        x={markerPoint.x}
                        y={markerPoint.y - 15}
                        fontSize="14"
                        fontWeight="bold"
                        fill="#d32f2f"
                        textAnchor="middle"
                      >
                        {result?.code}
                      </text>
                    </g>
                  )}
                </svg>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
